// src/contexts/ConversationContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { Conversation } from "@visitproof/types";
import { ConversationService } from "../services/ConversationService";
import { useAccount } from "./AccountContext";
import { doc, onSnapshot } from "firebase/firestore"; // Import Firestore methods
import { db } from "../firebase";

interface ConversationContextProps {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  deleteConversationById: (conversationId: string) => void;
  setSelectedConversation: (conversationId: string) => void;
  reloadConversations: (
    status: "draft" | "open" | "paid" | "uncollectible" | "all",
  ) => void;
  loading: boolean;
  error: string | null;
}

const ConversationContext = createContext<ConversationContextProps | undefined>(
  undefined,
);

export const useConversation = (): ConversationContextProps => {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error(
      "useConversation must be used within a ConversationProvider",
    );
  }
  return context;
};

export const ConversationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { account } = useAccount();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null); // Track the selected conversation ID

  const reloadConversations = async (
    status: "draft" | "open" | "paid" | "uncollectible" | "all",
  ) => {
    if (!account?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const conversationList = await ConversationService.getAllConversations(
        account.id,
        status,
      );
      setConversations(conversationList);
    } catch (err) {
      setError("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  const setSelectedConversationById = (conversationId: string) => {
    setSelectedConversationId(conversationId); // Update the selectedConversationId state
  };

  const deleteConversationById = async (conversationId: string) => {
    await ConversationService.deleteConversation(conversationId);
    setConversations(
      conversations.filter(
        (conversation) => conversation.id !== conversationId,
      ),
    );
  };

  useEffect(() => {
    if (!selectedConversationId) return;

    const docRef = doc(db, `conversations/${selectedConversationId}`);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const conversation = {
            id: docSnapshot.id,
            ...docSnapshot.data(),
          } as Conversation;
          setSelectedConversation(conversation);
        } else {
          setError(`Conversation with ID ${selectedConversationId} not found`);
        }
      },
      (err) => {
        setError(`Failed to listen to conversation changes: ${err.message}`);
      },
    );

    // Cleanup listener when component unmounts or when selectedConversationId changes
    return () => {
      unsubscribe();
    };
  }, [selectedConversationId]);

  return (
    <ConversationContext.Provider
      value={{
        conversations,
        selectedConversation,
        setSelectedConversation: setSelectedConversationById,
        reloadConversations,
        loading,
        error,
        deleteConversationById,
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
};
